import { useState } from 'react';
import { useQuery } from '@apollo/client';
import CopyToClipboard from 'react-copy-to-clipboard';
import moment from 'moment-timezone';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@rivallapp/volosports-components';
import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons/faArrowUpFromBracket';

import styles from './styles.module.scss';
import { siteUrl } from '../../../environment';
import { Button, ColorIndicator, Loading } from '../../../components';
import { LeagueContact } from '../../../features';
import { useActionStates } from '../../../hooks';
import { GroupProps, LeagueProps, RegistrantProps, TeamProps } from '../../../types';
import TEAM_SCHEDULE_QUERY from './TEAM_SCHEDULE_QUERY';
import { DailyProgramTypes } from '../../../shared/program-type-enum';
import { RegistrantTypeEnum } from '../../../shared/registrant-type-enum';

const cx = classNames.bind(styles);

const LeagueDetailsCard = ({ league, team, group, registrant }) => {
  const {
    _id: leagueId,
    programType,
    venue,
    start_date,
    deepLink,
    isDaily,
    allow_teams,
  } = league || {};

  const teamInfo = team || group || undefined;

  const { name: teamName, color = {} } = teamInfo || {};
  const { _id: groupId, deepLink: groupDeepLink = '' } = group || {};

  const { kind } = registrant || {};
  const individualLabel =
    kind === RegistrantTypeEnum.WAITLISTED_INDIVIDUAL ? 'Waitlisted' : 'Free Agent';

  const { setSuccess } = useActionStates({ withAlerts: true });

  let shareUrl = deepLink || `${siteUrl}/l/${leagueId}`;
  if (group) shareUrl = groupDeepLink || `${siteUrl}/g/${groupId}`;
  if (isDaily) shareUrl = deepLink || `${siteUrl}/d/${leagueId}`;

  const linkType = 'Invite link';

  const [didCopy, setDidCopy] = useState(false);

  const headerImg = league?.photoUrl;
  const displayName = league?.displayName;

  const startDate = new Date(start_date);
  const monthYear = startDate?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const { formatted_address, name } = venue || {};

  const query = (formatted_address || name || '').replace(/,/g, '').replace(/ /g, '+');

  const {
    data: teamScheduleData,
    loading: teamScheduleLoading,
    error: teamScheduleError,
  } = useQuery(TEAM_SCHEDULE_QUERY, {
    skip: !team || !league?.schedule_approved,
    fetchPolicy: 'cache-and-network',
    variables: { input: { teamId: team?._id } },
  });

  if (teamScheduleLoading) return <Loading />;
  if (teamScheduleError) throw new Error(teamScheduleError);

  const { teamSchedule } = teamScheduleData || {};

  const { wins, nextGame, losses, ties } = teamSchedule || {};

  return (
    <div className={cx('container')}>
      <div className={cx('programContainer')}>
        <div className={cx('detailsBackground', 'col-md-6')}>
          <h1 className={cx('displayName')}>{displayName}</h1>
          <h2 className={cx('monthYear')}>{monthYear}</h2>
          <h2 className={cx('teamHeading')}>
            {allow_teams && (teamInfo ? teamName : individualLabel)}
            {allow_teams && teamSchedule && (
              <span>
                {wins || 0}-{ties || 0}-{losses || 0}
              </span>
            )}
          </h2>
          <div className={cx('detailsContainer')}>
            {color.hex ? (
              <div className={cx('colorContainer')}>
                <ColorIndicator color={color} size="14px" addBorder />
                <div className={cx('colorLabel')}>{color.name}</div>
              </div>
            ) : null}
            {nextGame ? (
              <div className={cx('nextGameContainer')}>
                Next game: &nbsp;
                {moment(nextGame.dateStr, 'YY/MM/DD').format('dddd M/D')}
                &nbsp;@ {moment(nextGame.startTimeStr, 'HH:mm').format('h:mma')}
                &nbsp;vs. {nextGame?.opponent?.name || 'tbd'}
              </div>
            ) : null}
          </div>
          <div className={cx('leagueInfoContainer')}>
            <div className={cx('leagueInfoItem')}>
              <LeagueContact isButton leagueId={leagueId} />
            </div>
            <div className={cx('leagueInfoItem')}>
              <Button default small>
                <a
                  target="_blank"
                  href={`https://www.google.com/maps/dir/${query}`}
                  rel="noopener noreferrer"
                >
                  Directions
                </a>
              </Button>
            </div>
            <div className={cx('leagueInfoItem')}>
              <Button
                className={cx('leagueInfoButton')}
                default
                small
                to={DailyProgramTypes.includes(programType) ? `/d/${leagueId}` : `/l/${leagueId}`}
              >
                Program Page
              </Button>
            </div>
          </div>
        </div>
        <div className={cx('headerImageContainer', 'col-md-6')}>
          <img src={headerImg} alt="volo program" />
          <CopyToClipboard
            text={shareUrl}
            onCopy={() => {
              setDidCopy(true);
              setSuccess(`${linkType} copied!`);
            }}
          >
            <div className={cx('share')} data-for="c2c" data-tip={didCopy}>
              <Button className={cx('shareButton')} style={{ opacity: didCopy ? 0.7 : 1 }}>
                <FontAwesomeIcon icon={faArrowUpFromBracket} />
              </Button>
            </div>
          </CopyToClipboard>
        </div>
      </div>
    </div>
  );
};

LeagueDetailsCard.propTypes = {
  league: LeagueProps,
  team: TeamProps,
  group: GroupProps,
  registrant: RegistrantProps,
};

LeagueDetailsCard.defaultProps = {
  league: null,
  team: null,
  group: null,
  registrant: null,
};

export default LeagueDetailsCard;
